import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProducts } from 'contexts/Products';
import { SingleSelectField } from 'fields';
import { useField, useFormikContext } from 'formik';
import type { IGenericObject } from 'types';

export default function BagSize() {
  const { t } = useTranslation('CreateOrder', { keyPrefix: 'ProductForm' });

  const [, { value: paymentTermCode }, { setValue: setPaymentTermCode }] = useField(
    'paymentTerms.paymentTermsCode',
  );

  const {
    state,
    selectors: { selectGroupedProduct, selectProduct },
  } = useProducts();

  const { setValues, values } = useFormikContext<IGenericObject>();

  const currentSelectedProduct = values.itemGroupRef
    ? selectGroupedProduct(state, values.itemGroupRef)
    : null;
  const disabled = !values.itemGroupRef;

  const options = useMemo(() => {
    if (!currentSelectedProduct) return [];

    return currentSelectedProduct.packages.map((pck) => ({
      value: pck.packagedItemReference,
      label: pck.packageDescription,
    }));
  }, [currentSelectedProduct]);

  const setDefaultValue = useCallback(
    async (value, productData) => {
      await setValues(
        {
          ...values,
          packagedItemReference: value,
          productData,
        },
        true,
      );
    },
    [values, setValues],
  );

  const onChange = useCallback(
    async (value) => {
      await setValues(
        {
          ...values,
          packagedItemReference: value,
          productData: selectProduct(state, value) || {},
        },
        true,
      );

      if (paymentTermCode) {
        await setPaymentTermCode(''); // Reset payment term code when bag size is changed
      }
    },
    [setValues, values, selectProduct, state, paymentTermCode, setPaymentTermCode],
  );

  useEffect(() => {
    if (
      currentSelectedProduct &&
      currentSelectedProduct.packages.length === 1 &&
      !values.packagedItemReference
    ) {
      setDefaultValue(
        currentSelectedProduct.packages[0].packagedItemReference,
        currentSelectedProduct.packages[0],
      );
    }
  }, [currentSelectedProduct, values.packagedItemReference, setDefaultValue]);

  return (
    <SingleSelectField
      required
      disabled={disabled}
      data-testid="bag-size-select"
      name="packagedItemReference"
      label={t('labels.bagSize')}
      placeholder={t('placeholders.bagSize')}
      options={options}
      onChange={onChange}
    />
  );
}
